import { useState, type ReactElement } from 'react';
import { ActionIcon, Badge, Button, Divider, Group, Menu, Popover, Stack, Text } from '@mantine/core';
import { Check, GripVertical, Layers, Plus, X } from 'lucide-react';
import type { MegaGridColumn, MegaGridGroupBy } from './MegaGridTypes';

interface MegaGridGroupByEditorProps<T> {
    groupable: MegaGridColumn<T>[];
    groupBy: MegaGridGroupBy[];
    onAdd: (columnKey: string) => void;
    onRemove: (columnKey: string) => void;
    onReorder: (columnKeys: string[]) => void;
    onSetSort: (columnKey: string, sortMode: 'count' | 'value', sortDir: 'asc' | 'desc') => void;
    onClear: () => void;
}

const SORT_OPTIONS: { label: string; mode: 'count' | 'value'; dir: 'asc' | 'desc' }[] = [
    { label: 'Count (high → low)', mode: 'count', dir: 'desc' },
    { label: 'Count (low → high)', mode: 'count', dir: 'asc' },
    { label: 'Value (A → Z)', mode: 'value', dir: 'asc' },
    { label: 'Value (Z → A)', mode: 'value', dir: 'desc' },
];

function sortLabel(g: MegaGridGroupBy): string {
    if (g.sortMode === 'count') return g.sortDir === 'desc' ? 'Count ↓' : 'Count ↑';
    return g.sortDir === 'asc' ? 'A → Z' : 'Z → A';
}

export default function MegaGridGroupByEditor<T>({ groupable, groupBy, onAdd, onRemove, onReorder, onSetSort, onClear }: MegaGridGroupByEditorProps<T>): ReactElement {
    const [dragKey, setDragKey] = useState<string | null>(null);
    const byKey = new Map(groupable.map((c) => [c.key, c]));
    const available = groupable.filter((c) => !groupBy.some((g) => g.columnKey === c.key));

    const handleDrop = (targetKey: string): void => {
        if (!dragKey || dragKey === targetKey) return;
        const order = groupBy.map((g) => g.columnKey);
        const from = order.indexOf(dragKey);
        const to = order.indexOf(targetKey);
        if (from < 0 || to < 0) return;
        order.splice(from, 1);
        order.splice(to, 0, dragKey);
        onReorder(order);
        setDragKey(null);
    };

    return (
        <Popover position="bottom-start" withArrow shadow="md" width={280}>
            <Popover.Target>
                <Button size="xs" variant="default" leftSection={<Layers size={14} />} rightSection={groupBy.length > 0 ? <Badge size="xs" circle variant="filled" color="brand">{groupBy.length}</Badge> : undefined}>
                    Group
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                        Group by
                    </Text>

                    {groupBy.length === 0 && (
                        <Text size="xs" c="dimmed">
                            Not grouped.
                        </Text>
                    )}

                    <Stack gap={2}>
                        {groupBy.map((g) => {
                            const column = byKey.get(g.columnKey);
                            return (
                                <Group
                                    key={g.columnKey}
                                    gap={6}
                                    wrap="nowrap"
                                    px={4}
                                    py={3}
                                    draggable
                                    onDragStart={() => setDragKey(g.columnKey)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => handleDrop(g.columnKey)}
                                    style={{ borderRadius: 'var(--mantine-radius-sm)', background: dragKey === g.columnKey ? 'var(--bm-color-zinc-hover)' : undefined, cursor: 'grab' }}
                                >
                                    <GripVertical size={13} opacity={0.5} />
                                    <Text size="xs" style={{ flex: 1 }}>
                                        {column?.header ?? g.columnKey}
                                    </Text>
                                    <Menu position="bottom-end" shadow="md" width={190}>
                                        <Menu.Target>
                                            <Button size="compact-xs" variant="subtle" color="gray">
                                                {sortLabel(g)}
                                            </Button>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            {SORT_OPTIONS.map((opt) => {
                                                const selected = g.sortMode === opt.mode && g.sortDir === opt.dir;
                                                return (
                                                    <Menu.Item
                                                        key={opt.label}
                                                        leftSection={selected ? <Check size={13} /> : <span style={{ width: 13 }} />}
                                                        onClick={() => onSetSort(g.columnKey, opt.mode, opt.dir)}
                                                    >
                                                        {opt.label}
                                                    </Menu.Item>
                                                );
                                            })}
                                        </Menu.Dropdown>
                                    </Menu>
                                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => onRemove(g.columnKey)} aria-label={`Remove ${column?.header ?? g.columnKey} grouping`}>
                                        <X size={13} />
                                    </ActionIcon>
                                </Group>
                            );
                        })}
                    </Stack>

                    {available.length > 0 && (
                        <Menu position="bottom-start" shadow="md" width={220}>
                            <Menu.Target>
                                <Button size="compact-xs" variant="light" leftSection={<Plus size={13} />}>
                                    Add layer
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown mah={280} style={{ overflowY: 'auto' }}>
                                {available.map((c) => (
                                    <Menu.Item key={c.key} onClick={() => onAdd(c.key)}>
                                        {c.header}
                                    </Menu.Item>
                                ))}
                            </Menu.Dropdown>
                        </Menu>
                    )}

                    {groupBy.length > 0 && (
                        <>
                            <Divider />
                            <Button size="compact-xs" variant="subtle" color="gray" onClick={onClear}>
                                Clear grouping
                            </Button>
                        </>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
